import React, {
  createContext,
  use,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  TranscriptSegment,
  useDeepgramLiveTranscription,
} from '../hooks/useDeepgramLiveTranscription';
import { MeContext } from './me.context';

/**
 * App-wide live voice transcription.
 *
 * A single global session owns the microphone, streams audio to Deepgram for
 * live speaker-labeled transcription, and (best-effort) records the audio to a
 * Blob for upload. Any feature calls `useLiveTranscription().start(...)`; the
 * floating glass overlay (rendered by the provider) shows the session and
 * controls it, persisting across route changes.
 *
 * The live transcript is an optimization — `onComplete` always returns the
 * recorded Blob too, so a consumer that needs the audio (e.g. Voice to Chart)
 * can upload it and let the backend transcribe if the live stream was
 * unavailable (iOS/Safari) or partial.
 */

export type LiveTranscriptionPhase =
  | 'idle'
  | 'starting'
  | 'recording'
  | 'paused'
  | 'finalizing'
  | 'error';

export type LiveTranscriptionResult = {
  /** Speaker-labeled transcript ("Speaker 1: ...\nSpeaker 2: ..."), '' if none. */
  transcript: string;
  /** Recorded audio, or null if recording failed / produced nothing. */
  blob: Blob | null;
  /** MIME type the audio was recorded as (drives the upload extension). */
  mimeType: string;
  /** Wall-clock recording length in seconds (excludes paused time). */
  durationSeconds: number;
};

export type StartLiveTranscriptionOptions = {
  /** Label shown in the overlay (e.g. patient name or "Visit recording"). */
  title?: string;
  /** Called once the user stops and the transcript + blob are finalized. */
  onComplete?: (result: LiveTranscriptionResult) => void | Promise<void>;
  /** Called if the user cancels/discards the session. */
  onCancel?: () => void;
  /** Optional hard cap; the session auto-stops when reached. */
  maxDurationSeconds?: number;
};

type LiveTranscriptionContextType = {
  phase: LiveTranscriptionPhase;
  /** True while actively capturing (recording or paused) — i.e. overlay is up. */
  isActive: boolean;
  title: string;
  segments: TranscriptSegment[];
  interimSegments: TranscriptSegment[];
  elapsedSeconds: number;
  /** False on iOS/Safari (mp4) — recording still works, transcript comes later. */
  liveSupported: boolean;
  /** Web Audio analyser for the active mic stream; null when idle. */
  analyser: AnalyserNode | null;
  errorMessage: string | null;
  isMinimized: boolean;
  start: (options?: StartLiveTranscriptionOptions) => Promise<boolean>;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<void>;
  cancel: () => void;
  minimize: () => void;
  expand: () => void;
};

const LiveTranscriptionContext = createContext<LiveTranscriptionContextType | undefined>(undefined);

// 128 kbps, the wavesurfer RecordPlugin default this recorder replaced.
const RECORDING_BITRATE = 128000;

// iOS/Safari can't stream webm incrementally; record mp4 there (no live
// transcript — the backend batch path transcribes the uploaded blob).
const detectIsIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const LiveTranscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { me } = use(MeContext);
  const clinicId = me?.selectedClinic?.ID;

  // Stable per-mount: drives both the live stream codec and the recorded blob.
  const mimeType = useMemo(() => (detectIsIOS() ? 'audio/mp4' : 'audio/webm;codecs=opus'), []);

  const dgram = useDeepgramLiveTranscription({
    clinicId,
    mimeType,
    onError: () => {
      // Best-effort: the recorded blob still uploads and the backend transcribes.
      console.warn('[live-transcription] live stream unavailable; using recorded audio.');
    },
  });

  const [phase, setPhase] = useState<LiveTranscriptionPhase>('idle');
  const [title, setTitle] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  // The MIME the recorder actually used — Safari may ignore our requested type
  // and pick its own, which must drive the blob type and upload extension.
  const recordedMimeRef = useRef(mimeType);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxDurationRef = useRef<number | null>(null);
  const onCompleteRef = useRef<StartLiveTranscriptionOptions['onComplete']>(undefined);
  const onCancelRef = useRef<StartLiveTranscriptionOptions['onCancel']>(undefined);
  // Read inside the 1Hz timer without re-creating the interval each tick.
  const elapsedRef = useRef(0);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const teardownMedia = useCallback(() => {
    stopTimer();

    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch {
      // ignore
    }
    mediaRecorderRef.current = null;

    streamRef.current?.getTracks().forEach(track => {
      try {
        track.stop();
      } catch {
        // ignore
      }
    });
    streamRef.current = null;

    const ctx = audioCtxRef.current;
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => {});
    }
    audioCtxRef.current = null;
    setAnalyser(null);
  }, [stopTimer]);

  const buildBlob = useCallback((): Blob | null => {
    if (chunksRef.current.length === 0) return null;
    return new Blob(chunksRef.current, { type: recordedMimeRef.current });
  }, []);

  // Stop the blob recorder and resolve once its final chunk has flushed.
  const finalizeBlob = useCallback((): Promise<Blob | null> => {
    return new Promise(resolve => {
      const mr = mediaRecorderRef.current;
      if (!mr || mr.state === 'inactive') {
        resolve(buildBlob());
        return;
      }
      mr.onstop = () => resolve(buildBlob());
      try {
        mr.stop();
      } catch {
        resolve(buildBlob());
      }
    });
  }, [buildBlob]);

  const reset = useCallback(() => {
    chunksRef.current = [];
    elapsedRef.current = 0;
    maxDurationRef.current = null;
    setElapsedSeconds(0);
    setTitle('');
    setIsMinimized(false);
    setErrorMessage(null);
  }, []);

  // Declared before start() so the timer callback can reference it.
  const stopRef = useRef<() => Promise<void>>(async () => {});

  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSeconds(elapsedRef.current);
      const cap = maxDurationRef.current;
      if (cap && elapsedRef.current >= cap) {
        void stopRef.current();
      }
    }, 1000);
  }, [stopTimer]);

  const start = useCallback(
    async (options: StartLiveTranscriptionOptions = {}): Promise<boolean> => {
      // One global session at a time — ignore re-entrant starts.
      if (phase !== 'idle' && phase !== 'error') {
        console.warn('[live-transcription] a session is already active.');
        return false;
      }

      reset();
      onCompleteRef.current = options.onComplete;
      onCancelRef.current = options.onCancel;
      maxDurationRef.current = options.maxDurationSeconds ?? null;
      setTitle(options.title ?? 'Recording');
      setPhase('starting');

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        setErrorMessage('Microphone access was blocked. Check your browser permissions.');
        setPhase('error');
        return false;
      }
      streamRef.current = stream;

      // Live waveform source — drawn on a canvas, so updating it never re-renders.
      try {
        const AudioCtx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new AudioCtx();
        audioCtxRef.current = ctx;
        // Autoplay policy can start the context suspended; resume best-effort.
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const source = ctx.createMediaStreamSource(stream);
        const node = ctx.createAnalyser();
        node.fftSize = 256;
        source.connect(node);
        setAnalyser(node);
      } catch {
        // Waveform is decorative; carry on without it.
      }

      // Record the audio to a blob (the upload source of truth). Safari can
      // reject an explicit mimeType, so fall back to the browser default.
      // Bitrate cap keeps phone uploads small enough to survive (13021289181).
      let recorder: MediaRecorder | null = null;
      try {
        recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: RECORDING_BITRATE });
      } catch {
        try {
          recorder = new MediaRecorder(stream, { audioBitsPerSecond: RECORDING_BITRATE });
        } catch {
          recorder = null;
        }
      }
      if (recorder) {
        recordedMimeRef.current = recorder.mimeType || mimeType;
        mediaRecorderRef.current = recorder;
        recorder.ondataavailable = event => {
          if (event.data && event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };
        recorder.start(1000);
      } else {
        // No blob — live transcript may still work; consumer handles null blob.
        mediaRecorderRef.current = null;
      }

      // Live transcription (best-effort; no-op on iOS/Safari mp4).
      dgram.reset();
      void dgram.connect(stream);

      elapsedRef.current = 0;
      setElapsedSeconds(0);
      setPhase('recording');
      startTimer();
      return true;
    },
    [phase, reset, mimeType, dgram, startTimer]
  );

  const pause = useCallback(() => {
    if (phase !== 'recording') return;
    stopTimer();
    try {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.pause();
      }
    } catch {
      // ignore
    }
    dgram.pause();
    setPhase('paused');
  }, [phase, stopTimer, dgram]);

  const resume = useCallback(() => {
    if (phase !== 'paused') return;
    try {
      if (mediaRecorderRef.current?.state === 'paused') {
        mediaRecorderRef.current.resume();
      }
    } catch {
      // ignore
    }
    dgram.resume();
    setPhase('recording');
    startTimer();
  }, [phase, dgram, startTimer]);

  const stop = useCallback(async () => {
    if (phase !== 'recording' && phase !== 'paused') return;
    setPhase('finalizing');
    stopTimer();

    const durationSeconds = elapsedRef.current;
    const transcript = await dgram.stop().catch(() => '');
    const blob = await finalizeBlob();

    teardownMedia();

    const onComplete = onCompleteRef.current;
    onCompleteRef.current = undefined;
    onCancelRef.current = undefined;

    setPhase('idle');
    reset();

    if (onComplete) {
      await Promise.resolve(
        onComplete({ transcript, blob, mimeType: recordedMimeRef.current, durationSeconds })
      ).catch(err => console.error('[live-transcription] onComplete handler failed:', err));
    }
  }, [phase, stopTimer, dgram, finalizeBlob, teardownMedia, reset]);

  stopRef.current = stop;

  const cancel = useCallback(() => {
    if (phase === 'idle') return;
    stopTimer();
    void dgram.stop().catch(() => {});
    teardownMedia();
    const onCancel = onCancelRef.current;
    onCompleteRef.current = undefined;
    onCancelRef.current = undefined;
    setPhase('idle');
    reset();
    onCancel?.();
  }, [phase, stopTimer, dgram, teardownMedia, reset]);

  const minimize = useCallback(() => setIsMinimized(true), []);
  const expand = useCallback(() => setIsMinimized(false), []);

  // "Active" = a live capture is in flight (excludes idle and the error state),
  // so a feature's record button re-enables after a permission failure.
  const isActive =
    phase === 'starting' || phase === 'recording' || phase === 'paused' || phase === 'finalizing';

  // Guard against tab close / refresh losing an in-memory recording. In-app
  // navigation is safe (the overlay persists across routes), so this only
  // covers leaving the app entirely.
  useEffect(() => {
    if (phase === 'idle' || phase === 'error') return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [phase]);

  const value = useMemo<LiveTranscriptionContextType>(
    () => ({
      phase,
      isActive,
      title,
      segments: dgram.segments,
      interimSegments: dgram.interimSegments,
      elapsedSeconds,
      liveSupported: dgram.isSupported(),
      analyser,
      errorMessage,
      isMinimized,
      start,
      pause,
      resume,
      stop,
      cancel,
      minimize,
      expand,
    }),
    [
      phase,
      isActive,
      title,
      dgram,
      elapsedSeconds,
      analyser,
      errorMessage,
      isMinimized,
      start,
      pause,
      resume,
      stop,
      cancel,
      minimize,
      expand,
    ]
  );

  return (
    <LiveTranscriptionContext.Provider value={value}>{children}</LiveTranscriptionContext.Provider>
  );
};

export const useLiveTranscription = () => {
  const context = useContext(LiveTranscriptionContext);

  if (!context) {
    throw new Error('useLiveTranscription must be used within LiveTranscriptionProvider');
  }

  return context;
};
